import React from "react";
import moment from "moment";
import { Moment } from "moment";
import { FormattedMessage } from "react-intl";

import { Album } from "@music/models";
import { getConfigNumber } from "@util/config";
import { messages } from "./messages";
import { Chart, ChartType, LegendOptions, TooltipOptions } from "chart.js";

// Actions
export const GET_REQUEST: string = "Sales/GET_REQUEST";
export const GET_SUCCESS: string = "Sales/GET_SUCCESS";
export const GET_FAILURE: string = "Sales/GET_FAILURE";

export const DEMOGRAPHICS_GET_REQUEST: string = "Sales/DEMOGRAPHICS_GET_REQUEST";
export const DEMOGRAPHICS_GET_SUCCESS: string = "Sales/DEMOGRAPHICS_GET_SUCCESS";
export const DEMOGRAPHICS_GET_FAILURE: string = "Sales/DEMOGRAPHICS_GET_FAILURE";

export const PLAYLIST_TRENDS_GET_REQUEST: string = "Sales/PLAYLIST_TRENDS_GET_REQUEST";
export const PLAYLIST_TRENDS_GET_SUCCESS: string = "Sales/PLAYLIST_TRENDS_GET_SUCCESS";
export const PLAYLIST_TRENDS_GET_FAILURE: string = "Sales/PLAYLIST_TRENDS_GET_FAILURE";

export const HEADLINE_STATS_GET_REQUEST: string = "Sales/HEADLINE_STATS_GET_REQUEST";
export const HEADLINE_STATS_GET_SUCCESS: string = "Sales/HEADLINE_STATS_GET_SUCCESS";
export const HEADLINE_STATS_GET_FAILURE: string = "Sales/HEADLINE_STATS_GET_FAILURE";

export const STREAMS_DOWNLOADS_TOGGLE: string = "Sales/STREAMS_DOWNLOADS_TOGGLE";

export const DATE_RANGE_SET: string = "Sales/DATE_RANGE_SET";

export const COUNTRIES_SET: string = "Sales/COUNTRIES_SET";

export const CHART_LEGEND_RESET: string = "Sales/CHART_LEGEND_RESET";
export const CHART_LEGEND_UPDATE: string = "Sales/CHART_LEGEND_UPDATE";

// EARLIEST_DATE won't change but the latest might, so don't cache that one.
const EARLIEST_DATE: Moment = moment("2013-01-01");
// const LATEST_DATE: Moment = moment().subtract(2, "day").endOf("day");
export const MAX_FILTER_DAYS: number = getConfigNumber("MAX_FILTER_DAYS");

export const DEFAULT_PERIOD_DAYS = 30;
export const getEarliestDate = () => EARLIEST_DATE.clone();
export const getLatestDate = () => moment().subtract(2, "day").endOf("day");
export const getDefaultFromDate = () => getLatestDate().clone().subtract(DEFAULT_PERIOD_DAYS - 1, "day").startOf("day");

export const getChartLegendOptions = <TType extends ChartType>() => ({
    align: "start",
    labels: {
        usePointStyle: true,
        pointStyle: "circle",
        boxWidth: 8,
        boxHeight: 8,
        borderRadius: 4,
        padding: 12,
        font: {
            size: 12,
        },
        color: Chart.defaults.color as string,
    },
} as LegendOptions<TType>);

export const getChartTooltipOptions = <TType extends ChartType>() => ({
    mode: "index",
    intersect: false,
    position: "nearest",
    usePointStyle: true,
    padding: 8,
    titleMarginBottom: 8,
    bodySpacing: 6,
    footerMarginTop: 8,
    backgroundColor: "rgba(255,255,255, 0.8)",
    bodyColor: Chart.defaults.color as string,
    titleColor: Chart.defaults.color as string,
    footerColor: Chart.defaults.color as string,
    titleFont: {
        weight: "normal",
    },
    footerFont: {
        weight: "normal",
    },
    boxHeight: 8,
    boxWidth: 8,
    cornerRadius: 0,
} as TooltipOptions<TType>);

export interface DateRange {
    label: string | JSX.Element;
    from: Moment;
    to: Moment;
}

export const getDatePresets = (album?: Album): DateRange[] => {
    const latestDate = getLatestDate();
    const presets = [
        {
            label: <FormattedMessage {...messages.presetLast30Days} />,
            from:  getDefaultFromDate(),
            to:  latestDate.clone(),
        },
        {
            label: <FormattedMessage {...messages.presetThisMonth} />,
            from:  latestDate.clone().startOf("month"),
            to:  latestDate.clone(),
        },
        {
            label: <FormattedMessage {...messages.presetLastMonth} />,
            from: moment().subtract(1, "month").startOf("month"),
            to: moment().subtract(1, "month").endOf("month"),
        },
        {
            label: <FormattedMessage {...messages.presetPreviousMonth} />,
            from: moment().subtract(2, "month").startOf("month"),
            to: moment().subtract(2, "month").endOf("month"),
        },
        // {
        //     label: <FormattedMessage {...messages.presetThisWeek} />,
        //     from: moment().subtract(1, "week").startOf("week"),
        //     to: latestDate.clone(),
        // },
        {
            label: <FormattedMessage {...messages.presetLastWeek} />,
            from:  moment().clone().subtract(1, "week").startOf("week"),
            to:  moment().clone().subtract(1, "week").endOf("week"),
        },
        {
            label: <FormattedMessage {...messages.presetPreviousWeek} />,
            from: moment().subtract(2, "week").startOf("week"),
            to: moment().subtract(2, "week").endOf("week"),
        },
        {
            label: <FormattedMessage {...messages.presetLast3Months} />,
            from: moment().subtract(3, "months").startOf("month"),
            to: moment().subtract(1, "months").endOf("month"),
        },
        {
            label: <FormattedMessage {...messages.presetPrevious3Months} />,
            from: moment().subtract(6, "months").startOf("month"),
            to: moment().subtract(4, "months").endOf("month"),
        },
    ];
    if (album != null && moment().diff(album.releaseDate, "years") < 1) {
        presets.push({
            label: <FormattedMessage {...messages.presetSinceReleaseDate} />,
            from: moment(album.releaseDate),
            to: latestDate.clone(),
        });
    }
    return presets;
};
